import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import DOMPurify from "dompurify";
import DashboardLayout from "@/components/DashboardLayout";
import { api } from "@/lib/api";
import { Eye } from "lucide-react";

const DAYS = [
  { v: 1, l: "Monday" }, { v: 2, l: "Tuesday" }, { v: 3, l: "Wednesday" },
  { v: 4, l: "Thursday" }, { v: 5, l: "Friday" }, { v: 6, l: "Saturday" }, { v: 0, l: "Sunday" },
];
const TZ = ["UTC", "America/Toronto", "America/Montreal", "America/Halifax", "America/Winnipeg", "America/Regina", "America/Edmonton", "America/Vancouver", "America/St_Johns", "America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles"];

export default function SettingsPreferences() {
  const [prefs, setPrefs] = useState(null);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  useEffect(() => { api.get("/preferences").then((r) => setPrefs(r.data)); }, []);
  const on = (k) => (v) => setPrefs({ ...prefs, [k]: v });
  const save = async () => {
    setBusy(true);
    try { await api.put("/preferences", prefs); toast.success("Digest preferences saved."); }
    catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };
  const openPreview = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/digest/preview");
      if (data.has_data === false) {
        toast.info(data.message || "Upload inventory and sales data before previewing a digest.");
        return;
      }
      setPreview(data); setPreviewOpen(true);
    } catch (err) { toast.error(err?.response?.data?.detail || "Preview failed"); }
    finally { setBusy(false); }
  };

  if (!prefs) return <DashboardLayout><div className="text-sm text-[#3B424B]">Loading…</div></DashboardLayout>;

  return (
    <DashboardLayout>
      <h1 className="text-2xl sm:text-3xl font-bold" data-testid="prefs-heading">Digest preferences</h1>
      <p className="mt-1 text-sm text-[#3B424B]">Pick the day and time zone for your weekly digest, and choose which sections you want.</p>

      <div className="mt-6 tm-card p-6 bg-white space-y-6 max-w-2xl">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <div className="font-semibold">Send my digest</div>
            <div className="text-xs text-[#3B424B]">Turn off to stop receiving digest emails entirely.</div>
          </div>
          <label className="inline-flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={prefs.enabled} onChange={(e) => on("enabled")(e.target.checked)} className="h-4 w-4 accent-[#14624A]" data-testid="prefs-enabled" />
            <span className="text-sm">{prefs.enabled ? "Enabled" : "Paused"}</span>
          </label>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div><label className="tm-label">Preferred day</label>
            <select className="tm-input" value={prefs.preferred_day} onChange={(e) => on("preferred_day")(Number(e.target.value))} disabled={!prefs.enabled} data-testid="prefs-day">
              {DAYS.map((d) => <option key={d.v} value={d.v}>{d.l}</option>)}
            </select></div>
          <div><label className="tm-label">Time zone</label>
            <select className="tm-input" value={prefs.timezone} onChange={(e) => on("timezone")(e.target.value)} disabled={!prefs.enabled} data-testid="prefs-timezone">
              {TZ.map((z) => <option key={z} value={z}>{z}</option>)}
            </select></div>
        </div>
        <div>
          <div className="tm-label">Include sections</div>
          <div className="mt-2 grid gap-2">
            {[["include_reorder", "Reorder priority"], ["include_slow", "Capital tied up (slow inventory)"], ["include_pricing", "Pricing signals"]].map(([k, l]) => (
              <label key={k} className="inline-flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={prefs[k]} onChange={(e) => on(k)(e.target.checked)} disabled={!prefs.enabled} className="h-4 w-4 accent-[#14624A]" data-testid={`prefs-${k}`} />
                <span className="text-sm">{l}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="pt-2 border-t border-[var(--tm-border)]">
          <div className="font-semibold">SMS stock-out alerts</div>
          <div className="text-xs text-[#3B424B]">Get a text when a fast-moving SKU drops below 7 days of supply. Emails always fire; SMS is an optional escalation.</div>
          <div className="mt-3 grid gap-3 sm:grid-cols-[1fr,auto] items-end">
            <div>
              <label className="tm-label">Mobile number (E.164, e.g. +14165551234)</label>
              <input
                type="tel"
                className="tm-input"
                placeholder="+14165551234"
                value={prefs.phone_number || ""}
                onChange={(e) => on("phone_number")(e.target.value)}
                data-testid="prefs-phone"
              />
            </div>
            <label className="inline-flex items-center gap-2 cursor-pointer pb-2.5">
              <input
                type="checkbox"
                checked={!!prefs.sms_enabled}
                onChange={(e) => on("sms_enabled")(e.target.checked)}
                className="h-4 w-4 accent-[#14624A]"
                data-testid="prefs-sms-enabled"
              />
              <span className="text-sm">SMS on</span>
            </label>
          </div>
        </div>

        <div className="pt-2 flex flex-wrap gap-3">
          <button className="tm-btn-primary" onClick={save} disabled={busy} data-testid="prefs-save">{busy ? "Saving…" : "Save preferences"}</button>
          <button className="tm-btn-outline" onClick={openPreview} disabled={busy} data-testid="prefs-preview"><Eye size={15} /> Preview next digest</button>
        </div>
      </div>

      {previewOpen && preview && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="digest-preview-modal" onClick={() => setPreviewOpen(false)}>
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="p-5 border-b border-[var(--tm-border)] flex items-center justify-between">
              <div><div className="text-xs uppercase tracking-wider text-[#3B424B] font-semibold">Preview · Subject</div><div className="font-semibold mt-1" data-testid="digest-preview-subject">{preview.subject}</div></div>
              <button className="tm-btn-outline h-9 px-3" onClick={() => setPreviewOpen(false)} data-testid="digest-preview-close">Close</button>
            </div>
            <div className="flex-1 overflow-y-auto tm-scroll" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(preview.html || "", { USE_PROFILES: { html: true } }) }} />
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
